import { watch, onUnmounted } from 'vue'
import { storeToRefs } from 'pinia'
import { useAuthStore } from '@/modules/auth'
import { usePortalBrandingStore } from '@/shared/stores/portalBranding'

const EMBED_ROOT_ID = 'reseller-body-embed'

/**
 * Injects the authenticated user's reseller body_embed_code before </body>,
 * matching legacy PortalService::bodyEmbedCode — only when the user belongs
 * to the portal domain's reseller.
 */
export function useBodyEmbedCode() {
  const authStore = useAuthStore()
  const portalBranding = usePortalBrandingStore()
  const { portalConfig } = storeToRefs(portalBranding)

  const clearEmbed = () => {
    document.getElementById(EMBED_ROOT_ID)?.remove()
  }

  const injectHtml = (html: string) => {
    clearEmbed()
    if (!html.trim()) return

    const root = document.createElement('div')
    root.id = EMBED_ROOT_ID
    root.setAttribute('data-reseller-body-embed', 'true')

    const staging = document.createElement('div')
    staging.innerHTML = html

    Array.from(staging.childNodes).forEach((node) => {
      if (node.nodeName === 'SCRIPT') {
        const oldScript = node as HTMLScriptElement
        const script = document.createElement('script')
        Array.from(oldScript.attributes).forEach((attr) => {
          script.setAttribute(attr.name, attr.value)
        })
        script.textContent = oldScript.textContent
        root.appendChild(script)
      } else {
        root.appendChild(node.cloneNode(true))
      }
    })

    document.body.appendChild(root)
  }

  const syncEmbed = () => {
    const user = authStore.user
    const portalResellerId = portalConfig.value?.reseller_id
    const userResellerId = user?.reseller_id

    const belongsToPortalReseller =
      !!user &&
      portalResellerId != null &&
      userResellerId != null &&
      String(portalResellerId) === String(userResellerId)

    const code = belongsToPortalReseller ? user?.reseller_body_embed_code : null

    if (code) {
      injectHtml(code)
    } else {
      clearEmbed()
    }
  }

  const stop = watch(
    () =>
      [
        authStore.isAuthenticated,
        authStore.user?.reseller_id,
        authStore.user?.reseller_body_embed_code,
        portalConfig.value?.reseller_id
      ] as const,
    () => syncEmbed(),
    { immediate: true }
  )

  onUnmounted(() => {
    stop()
    clearEmbed()
  })
}
